import React, { useState, useEffect, FocusEvent, useRef, useMemo } from 'react';
import Select, {
  InputActionMeta,
  SingleValue,
  SelectInstance,
  components,
  InputProps,
  DropdownIndicatorProps,
  ActionMeta,
  ClearIndicatorProps,
} from 'react-select';
import { useTranslation } from 'react-i18next';
import { fetchAndSetCountryList } from 'redux/modules/Location/location';
import { useAppSelector, useAppDispatch } from 'redux/hooks';
import { locationSelectors } from 'redux/modules/Location';
import { ICountry } from 'interfaces';
import { countryTranslate } from 'utils/localizationTranslate';
import {
  CustomDropdownIndicator,
  CustomDropdownIndicatorUpside,
  CountryCustomOption,
  getCustomValueContainer,
} from '../SelectorComponents';
import { CountrySelectStyles } from '../../LocationSelectors.styles';

const CountrySelectInput = <Option, IsMulti extends boolean>(
  props: InputProps<Option, IsMulti>,
) => <components.Input {...props} data-testid='countryDropdownInput' />;

export const getDropdownIndicator = (isUpside: boolean) => {
  const DropdownIndicatorWithTestId = <Option, IsMulti extends boolean>(
    props: DropdownIndicatorProps<Option, IsMulti>,
  ) => {
    if (props.selectProps.isClearable) {
      return null;
    }

    const Component = isUpside
      ? CustomDropdownIndicatorUpside
      : CustomDropdownIndicator;

    return <Component {...props} data-testid='countryDropdownToggle' />;
  };

  return DropdownIndicatorWithTestId;
};

export const CustomClearIndicator = (
  props: ClearIndicatorProps<any, false>,
) => {
  const { innerProps, ...rest } = props;

  const stop = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const callMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    stop(e);
    innerProps?.onMouseDown?.(e);
  };

  return (
    <components.ClearIndicator
      {...rest}
      innerProps={{
        ...innerProps,
        onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => {
          callMouseDown(e);
        },
        onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => {
          callMouseDown(e);
        },
      }}
    />
  );
};

type CountrySelectorProps = {
  changeCountry: (country: ICountry) => void;
  country: ICountry | undefined;
  includeLocationIcon?: boolean;
};

export const CountrySelector = ({
  changeCountry,
  country,
  includeLocationIcon,
}: CountrySelectorProps) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const dispatch = useAppDispatch();

  const selectRef = useRef<SelectInstance<ICountry>>(null);

  const countryList = useAppSelector(locationSelectors.getCountryList);
  const currentLocation = useAppSelector(locationSelectors.getCurrentLocation);

  useEffect(() => {
    if (!countryList) dispatch(fetchAndSetCountryList());
  }, []);

  const translatedCountry = countryTranslate(
    language,
    country ?? currentLocation?.country,
  );
  const [inputValue, setInputValue] = useState<string>(translatedCountry);
  const [isMenuOpened, setIsMenuOpened] = useState<boolean>(false);

  const placeholder = t('locationPage.enterCountry');

  const CustomValueContainer = useMemo(
    () => getCustomValueContainer(includeLocationIcon),
    [includeLocationIcon],
  );

  const DropdownIndicator = useMemo(
    () => getDropdownIndicator(isMenuOpened),
    [isMenuOpened],
  );

  const selectComponents = useMemo(
    () => ({
      Input: CountrySelectInput,
      DropdownIndicator,
      ValueContainer: CustomValueContainer,
      IndicatorSeparator: () => null,
      Option: CountryCustomOption,
      ClearIndicator: CustomClearIndicator,
    }),
    [DropdownIndicator, CustomValueContainer],
  );

  const sortCountries = (countries: ICountry[] | undefined, input: string) => {
    if (!countries) return undefined;
    const inputLower = input.toLowerCase();

    const startsWith = countries
      .filter((c) =>
        countryTranslate(language, c).toLowerCase().startsWith(inputLower),
      )
      .sort((a, b) =>
        countryTranslate(language, a).localeCompare(
          countryTranslate(language, b),
        ),
      );

    const contains = countries
      .filter(
        (c) =>
          countryTranslate(language, c).toLowerCase().includes(inputLower) &&
          !countryTranslate(language, c).toLowerCase().startsWith(inputLower),
      )
      .sort((a, b) =>
        countryTranslate(language, a).localeCompare(
          countryTranslate(language, b),
        ),
      );

    return [...startsWith, ...contains];
  };

  const options = sortCountries(countryList, inputValue);

  useEffect(() => {
    setInputValue(translatedCountry);
  }, [language, country]);

  const onClear = (actionMeta: ActionMeta<ICountry>) => {
    if (actionMeta.action === 'clear') {
      setInputValue('');
      setIsMenuOpened(true);
    }
  };
  const onOptionChange = (
    newCountry: SingleValue<ICountry>,
    actionMeta: ActionMeta<ICountry>,
  ) => {
    onClear(actionMeta);
    if (!newCountry) return;
    const translated = countryTranslate(language, newCountry);
    setInputValue(translated);
    changeCountry(newCountry);
    selectRef.current?.blur();
  };

  const onInputChange = (newValue: string, { action }: InputActionMeta) => {
    if (action === 'input-change') {
      setInputValue(newValue);
    }
  };

  const onInputFocus = (e: FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      e.target.select();
    }, 100);
  };

  const handleBlur = () => {
    if (!inputValue) {
      if (country) {
        setInputValue(countryTranslate(language, country));
      } else if (currentLocation?.country) {
        setInputValue(countryTranslate(language, currentLocation.country));
      }
    }
  };
  return (
    <Select
      className='notranslate'
      ref={selectRef}
      value={country}
      inputValue={inputValue}
      isClearable={inputValue !== ''}
      options={options}
      isMulti={false}
      onChange={onOptionChange}
      onInputChange={onInputChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      noOptionsMessage={() => t('locationPage.notFoundCountry')}
      components={selectComponents}
      styles={CountrySelectStyles}
      getOptionLabel={(option) => countryTranslate(language, option)}
      getOptionValue={(opt) => `${opt.id}`}
      isSearchable
      controlShouldRenderValue={false}
      backspaceRemovesValue={false}
      onFocus={onInputFocus}
      onMenuOpen={() => setIsMenuOpened(true)}
      onMenuClose={() => setIsMenuOpened(false)}
      openMenuOnFocus
    />
  );
};
